// src/lib/livret-qr-pdf.js — Phase A.6 (ADR-118) · refonte design L4 (2026-06-14)
//
// Génère un PDF imprimable A4 portrait : QR code pointant vers /livret/qr/<slug>,
// sur le design maquette FBZ — page entièrement SKY, logo FBZ BLANC haut-gauche,
// QR BLANC (modules blancs, fond transparent), texte bas en escalier serif
// italic blanc « Livret d'accueil & boutique d'extras ».
//
// Approche : génération 100% client (Option A ADR-118).
// Libs lazy-loaded : qrcode (data URL) + jsPDF (PDF builder).
//
// IMPORTANT : la GÉNÉRATION du QR (données = vrai /livret/qr/<slug>) est
// INCHANGÉE — seuls la couleur (blanc) et le design de la page changent.
//
// Contrainte jsPDF v2 : fonts Latin-1 (Helvetica/Times/Courier). Texte sanitizé
// via sanitizeForPdf() (apostrophes courbes / tirets em / guillemets typo).

// Logo BLANC pré-généré (variante de LOGO_B64, pixels opaques → blanc) pour le
// design maquette (logo blanc sur fond sky). Pas de recolor runtime → marche
// partout (navigateur, Node, jsdom) à l'identique.
const { LOGO_WHITE_B64 } = require("../assets/logo");
const { resolveAppBaseUrl } = require("./app-base-url");

const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const MARGIN_MM = 20;
const LOGO_SIZE_MM = 24; // logo FBZ haut-gauche

// ─── Lisibilité du QR à l'impression (2026-07-28) ────────────────────────────
// Le QR est imprimé et affiché dans le logement : le SEUL levier de fiabilité
// de scan est la TAILLE D'UN MODULE EN MILLIMÈTRES (surface / nb de modules).
// L'URL encodée est INCHANGÉE (/livret/qr/<slug>) — les QR déjà imprimés
// restent valides et un nouveau PDF encode exactement la même chaîne.
//
// 3 réglages, tous 100 % applicatifs (aucune migration, aucun backend) :
//
// 1) QR_SIZE_MM 90 → 110 : +22 % de côté à nombre de modules constant.
//
// 2) QR_IMAGE_MARGIN_MODULES 4 → 0 : la « quiet zone » n'est PAS supprimée,
//    elle est simplement DÉPORTÉE sur la page. L'image PNG est générée à fond
//    perdu (modules blancs, fond transparent) et posée sur un aplat sky plein
//    page : la zone de silence réelle autour du symbole est celle de la mise en
//    page (≥ 20 mm partout, cf. computeQrPlacement) — très au-delà des
//    4 modules exigés par la norme. Garder margin:4 dans l'image revenait à
//    payer 8 modules de vide DANS le carré de 110 mm, soit ~20 % de la surface
//    utile perdue pour une zone de silence déjà fournie par le fond.
//    ⚠️ Corollaire : ne jamais rogner le fond sky autour du QR ni extraire ce
//    PNG pour l'afficher sur un fond non uniforme sans re-ajouter la marge.
//
// 3) QR_SCALE 8 → 32 px/module : à 110 mm, 8 px/module ne donnait que ~65 dpi
//    → les bords de modules étaient ré-interpolés (flous) par le rasteriseur
//    d'impression. 32 px/module donne 240-275 dpi pour les versions QR
//    réalistes. Coût : ~12 Ko de PNG, invisible sur le poids du PDF.
//
// Niveau de correction d'erreur : MAINTENU à 'M'. Passer à 'Q' rend le symbole
// plus tolérant aux salissures MAIS ajoute de la redondance donc des modules,
// ce qui RÉDUIT la taille de chaque module à surface constante. Mesuré sur
// l'URL canonique https://sejour.fullbnbzen.com/livret/qr/<slug> :
//   - slug court « lc1 »  (43 car.) : M → v4 (33 modules) · Q → v4 (33) — égalité
//   - slug long « appartement-les-augustins-toulouse » (74 car.) :
//       M → v5 (37 modules, module = 2,973 mm) · Q → v6 (41, module = 2,683 mm)
//   - Q est ≥ M en version pour TOUTE longueur de slug ≥ 7 caractères.
// Conclusion : 'Q' ne fait jamais gagner de module et en fait perdre ~10 % dès
// qu'un slug est un peu long → on garde 'M' + agrandissement.
const QR_SIZE_MM = 110; // QR centré (était 90 — cf. bloc ci-dessus)
const QR_ERROR_CORRECTION_LEVEL = "M"; // NE PAS monter à Q/H (réduit le module)
const QR_IMAGE_MARGIN_MODULES = 0; // quiet zone assurée par le fond sky de la page
const QR_SCALE = 32; // px par module dans le PNG source (netteté d'impression)
const QR_VERTICAL_OFFSET_MM = 18; // remonte le QR au-dessus du centre (maquette)

// Métriques de mise en page utilisées par computeQrPlacement().
const PT_TO_MM = 25.4 / 72;
const LABEL_FONT_PT = 15; // « FULLBNBZEN » sous le logo
const TITLE_FONT_PT = 34; // texte escalier « Livret d'accueil & … »
const TITLE_GAP_MM = 40; // écart bas du QR → 1re ligne du texte escalier
const DESCENDER_RATIO = 0.3; // approximations conservatrices (encre réelle <)
const ASCENDER_RATIO = 1.0;
const MIN_QUIET_ZONE_MODULES = 4; // exigence ISO/IEC 18004

// Charte FBZ public-facing (ADR-149) — RGB pour jsPDF.
const COLOR_SKY = [135, 184, 230]; // #87b8e6 — fond plein page
const COLOR_WHITE = [255, 255, 255];

function sanitizeForPdf(str) {
  return String(str || "")
    .replace(/[‘’]/g, "'") // apostrophes courbes → droite
    .replace(/[“”]/g, '"') // guillemets courbes → droits
    .replace(/[–—]/g, "-"); // tirets em/en → tiret
}

// Lot INFRA 2.1 PR-A — le QR imprimé doit pointer le domaine CANONIQUE, plus
// jamais l'origin du navigateur (un PDF généré depuis un alias vercel.app ou
// une preview imprimait une URL non canonique). Même résolution que
// buildLivretUrl client : REACT_APP_FBZ_APP_BASE_URL > dev local > fallback
// dur https://sejour.fullbnbzen.com (cf. src/lib/app-base-url.js).
function getBaseUrl() {
  return resolveAppBaseUrl();
}

/**
 * Géométrie du QR sur la page + zones de silence effectives (mm).
 *
 * Fonction PURE : sert à la fois au rendu (positionnement réel) et aux tests
 * (garde-fou anti-régression sur la quiet zone). Les clairances sont mesurées
 * jusqu'à la prochaine ENCRE (bas du libellé FULLBNBZEN au-dessus, haut de la
 * 1re ligne du texte escalier en dessous, bords de page à gauche/droite) —
 * tout le reste est du sky uniforme, donc de la zone de silence valide.
 *
 * @returns {{x:number,y:number,size:number,titleBaselineY:number,
 *            clearanceLeftMm:number,clearanceRightMm:number,
 *            clearanceTopMm:number,clearanceBottomMm:number,
 *            minClearanceMm:number}}
 */
function computeQrPlacement() {
  const size = QR_SIZE_MM;
  const x = (PAGE_WIDTH_MM - size) / 2;
  const y = (PAGE_HEIGHT_MM - size) / 2 - QR_VERTICAL_OFFSET_MM;

  // Bas de l'encre du libellé « FULLBNBZEN » (baseline + descendante).
  const labelInkBottom =
    MARGIN_MM + LOGO_SIZE_MM + 9 + LABEL_FONT_PT * PT_TO_MM * DESCENDER_RATIO;
  // Haut de l'encre de la 1re ligne du texte escalier (baseline - ascendante).
  const titleBaselineY = y + size + TITLE_GAP_MM;
  const titleInkTop = titleBaselineY - TITLE_FONT_PT * PT_TO_MM * ASCENDER_RATIO;

  const clearanceLeftMm = x;
  const clearanceRightMm = PAGE_WIDTH_MM - (x + size);
  const clearanceTopMm = y - labelInkBottom;
  const clearanceBottomMm = titleInkTop - (y + size);

  return {
    x,
    y,
    size,
    titleBaselineY,
    clearanceLeftMm,
    clearanceRightMm,
    clearanceTopMm,
    clearanceBottomMm,
    minClearanceMm: Math.min(
      clearanceLeftMm,
      clearanceRightMm,
      clearanceTopMm,
      clearanceBottomMm,
    ),
  };
}

/**
 * Génère le PDF QR du livret (design maquette L4).
 * @param {object} args
 * @param {string} args.slug - slug du livret (route /livret/qr/<slug>)
 * @param {string} [args.title] - non utilisé (design statique) ; conservé pour
 *   compat d'appel (LivretQrDownloadButton). Idem coverPhotoUrl (ignoré).
 * @param {string} [args.baseUrl] - override base URL (tests)
 * @returns {Promise<Blob>} PDF blob
 */
async function generateLivretQrPdf({ slug, baseUrl } = {}) {
  if (!slug || typeof slug !== "string") {
    throw new Error("slug requis");
  }

  const base = baseUrl || getBaseUrl();
  const qrUrl = `${base}/livret/qr/${encodeURIComponent(slug)}`;

  // Lazy-load libs en parallèle (pessimist F-4).
  const [qrcodeMod, jspdfMod] = await Promise.all([
    import("qrcode"),
    import("jspdf"),
  ]);
  const QRCode = qrcodeMod.default || qrcodeMod;
  const jsPDF = jspdfMod.jsPDF || jspdfMod.default;

  // 1. QR — modules BLANCS, fond TRANSPARENT (le sky de la page transparaît).
  //    Données = vrai QR du logement (/livret/qr/<slug>) — INCHANGÉ.
  const qrDataUrl = await QRCode.toDataURL(qrUrl, {
    errorCorrectionLevel: QR_ERROR_CORRECTION_LEVEL,
    margin: QR_IMAGE_MARGIN_MODULES,
    scale: QR_SCALE,
    color: { dark: "#FFFFFF", light: "#00000000" },
  });

  // 2. PDF A4 portrait — maquette : fond sky plein page.
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  doc.setFillColor(...COLOR_SKY);
  doc.rect(0, 0, PAGE_WIDTH_MM, PAGE_HEIGHT_MM, "F");

  // Logo blanc haut-gauche + libellé FULLBNBZEN (blanc) dessous.
  doc.addImage(LOGO_WHITE_B64, "PNG", MARGIN_MM, MARGIN_MM, LOGO_SIZE_MM, LOGO_SIZE_MM);
  doc.setTextColor(...COLOR_WHITE);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(LABEL_FONT_PT);
  doc.text("FULLBNBZEN", MARGIN_MM, MARGIN_MM + LOGO_SIZE_MM + 9, {
    align: "left",
  });

  // QR centré horizontalement, légèrement au-dessus du centre vertical.
  // Le fond sky autour du QR EST la zone de silence (cf. computeQrPlacement).
  const placement = computeQrPlacement();
  doc.addImage(qrDataUrl, "PNG", placement.x, placement.y, placement.size, placement.size);

  // Texte bas en escalier, blanc, serif italic (Times — proche Playfair charte).
  doc.setTextColor(...COLOR_WHITE);
  doc.setFont("times", "italic");
  doc.setFontSize(TITLE_FONT_PT);
  const baseY = placement.titleBaselineY;
  doc.text(sanitizeForPdf("Livret d'accueil"), MARGIN_MM + 8, baseY, {
    align: "left",
  });
  doc.text("&", PAGE_WIDTH_MM / 2, baseY + 20, { align: "center" });
  doc.text(
    sanitizeForPdf("boutique d'extras"),
    PAGE_WIDTH_MM - MARGIN_MM - 8,
    baseY + 40,
    { align: "right" },
  );

  return doc.output("blob");
}

// Configuration FIGÉE du QR imprimé — exposée pour le test anti-régression
// (toute modification doit être un choix explicite, pas une dérive silencieuse).
const QR_PRINT_CONFIG = Object.freeze({
  sizeMm: QR_SIZE_MM,
  errorCorrectionLevel: QR_ERROR_CORRECTION_LEVEL,
  imageMarginModules: QR_IMAGE_MARGIN_MODULES,
  scale: QR_SCALE,
  minQuietZoneModules: MIN_QUIET_ZONE_MODULES,
  pageWidthMm: PAGE_WIDTH_MM,
  pageHeightMm: PAGE_HEIGHT_MM,
});

module.exports = {
  generateLivretQrPdf,
  sanitizeForPdf,
  getBaseUrl,
  computeQrPlacement,
  QR_PRINT_CONFIG,
};
